import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, SpaceBetween } from '@cloudscape-design/components';
import { apiFetch } from '../auth/apiClient';
import { readPublishResult } from './publishResult';

function newRequestId() {
  return window.crypto?.randomUUID?.() || `publish-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
const label = value => typeof value === 'string' ? value : (value?.en || value?.fr || 'Step');
const display = value => value === undefined ? '(absent)' : typeof value === 'string' ? value : JSON.stringify(value, null, 2);
function describePath(path) {
  return path.replace(/^components\.(\d+)/, (_, index) => `Item ${Number(index) + 1}`)
    .replace(/\.accept$/, ': accepted file types').replace(/\.en$/, ' (English)').replace(/\.fr$/, ' (French)') || 'Step contents';
}

export default function WorkflowPublicationReview({ visible, workflowId, onDismiss, onPublished }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [stale, setStale] = useState(false);
  const [revision, setRevision] = useState(0);
  const inFlight = useRef(false);
  useEffect(() => {
    if (!visible || !workflowId) return undefined;
    let cancelled = false;
    setReview(null); setError(''); setStale(false); setLoading(true);
    (async () => {
      try {
        const response = await apiFetch(`/api/workflows/${workflowId}/publication-preview`);
        const data = await readPublishResult(response);
        if (!data.expected || !Array.isArray(data.changes)) throw new Error('The publication comparison is unavailable.');
        if (!cancelled) setReview({ ...data, requestId: newRequestId() });
      } catch (err) { if (!cancelled) setError(err.message || 'Unable to load the publication comparison.'); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [visible, workflowId, revision]);
  async function publish() {
    if (!review || stale || inFlight.current) return;
    inFlight.current = true; setPublishing(true); setError('');
    try {
      const response = await apiFetch(`/api/workflows/${workflowId}/publish`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expected: review.expected, requestId: review.requestId }),
      });
      const data = await readPublishResult(response);
      onPublished(data);
      onDismiss();
    } catch (err) {
      setError(err.message || 'Publication could not be confirmed. Retry to check its result.');
      if (err.status === 409) setStale(true);
    } finally { inFlight.current = false; setPublishing(false); }
  }
  return <Modal visible={visible} onDismiss={() => { if (!inFlight.current) onDismiss(); }} header="Review intake publication" size="large"
    footer={<SpaceBetween direction="horizontal" size="xs">
      <Button onClick={onDismiss} disabled={publishing}>Cancel</Button>
      <Button onClick={() => setRevision(value => value + 1)} disabled={publishing || loading}>Refresh comparison</Button>
      <Button variant="primary" onClick={publish} loading={publishing} disabled={!review || loading || stale}>Publish reviewed changes</Button>
    </SpaceBetween>}>
    <SpaceBetween size="m">
      <p>Publishing replaces the current public intake with this saved workflow. Review the changes below. The previous publication will be retained for recovery.</p>
      {loading && <p role="status">Loading publication comparison…</p>}
      {error && <Alert type="error">{error}</Alert>}
      {review && <>
        <p>{review.steps} steps after publication. {review.changes.length ? 'Changes from the current public intake:' : 'The intake steps are unchanged.'}</p>
        {review.changes.map((change, index) => <details key={`${change.stepId}-${index}`}>
          <summary>{change.kind === 'added' ? 'Added' : change.kind === 'removed' ? 'Removed' : 'Changed'}: {label(change.title)}</summary>
          {change.fields.map((field, fieldIndex) => <div key={fieldIndex}>
            <strong>{describePath(field.path)}</strong>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div><p>Current</p><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{display(field.before)}</pre></div>
              <div><p>After publication</p><pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{display(field.after)}</pre></div>
            </div>
          </div>)}
        </details>)}
      </>}
    </SpaceBetween>
  </Modal>;
}
